import { useState, useEffect, useCallback, type FC } from "react";
import { apiWithAuth } from "../../services/utils";
import {
  Breadcrumb,
  Button,
  Card,
  Label,
  Modal,
  Spinner,
  Table,
  TextInput,
  Badge,
} from "flowbite-react";
import {
  HiHome,
  HiPlus,
  HiRefresh,
  HiTrash,
  HiClock,
  HiCheckCircle,
  HiXCircle,
  HiOutlineDeviceMobile,
  HiOutlineClipboardCopy,
} from "react-icons/hi";
import { FiMessageCircle, FiWifi, FiWifiOff, FiActivity, FiShield } from "react-icons/fi";
import toast from "react-hot-toast";

type WhatsAppDevice = {
  id: number;
  deviceId: string;
  deviceName: string | null;
  status: "ONLINE" | "OFFLINE";
  lastSeenAt: string | null;
  createdAt: string;
  updatedAt: string;
};

const WhatsAppDevices: FC = () => {
  const [devices, setDevices] = useState<WhatsAppDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [newDevice, setNewDevice] = useState({
    deviceId: "",
    deviceName: "",
  });
  const [authenticating, setAuthenticating] = useState(false);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [authenticatedDevice, setAuthenticatedDevice] = useState<WhatsAppDevice | null>(null);

  const fetchDevices = useCallback(async () => {
    const token = sessionStorage.getItem("token");
    if (!token) return;
    setLoading(true);
    try {
      const response = await apiWithAuth.get("/whatsapp/devices");
      if (response.data && Array.isArray(response.data.devices || response.data)) {
        const devicesArray = Array.isArray(response.data.devices)
          ? response.data.devices
          : response.data;
        setDevices(devicesArray);
      } else {
        setDevices([]);
      }
    } catch (error: any) {
      console.error("Error fetching devices:", error);
      toast.error(error.response?.data?.message || "Failed to fetch devices");
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchDevices();
    const interval = setInterval(() => {
      void fetchDevices();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchDevices]);

  const handleAuthenticate = async () => {
    const token = sessionStorage.getItem("token");
    if (!token || !newDevice.deviceId.trim()) {
      toast.error("Device ID is required");
      return;
    }

    setAuthenticating(true);
    try {
      const response = await apiWithAuth.post("/whatsapp/devices/auth", {
        deviceId: newDevice.deviceId.trim(),
        deviceName: newDevice.deviceName.trim() || undefined,
      });

      if (response.data) {
        // Save to localStorage for persistence
        localStorage.setItem("whatsapp_authToken", response.data.authToken);
        localStorage.setItem("whatsapp_authenticatedDevice", JSON.stringify(response.data.device));

        setAuthToken(response.data.authToken);
        setAuthenticatedDevice(response.data.device);
        toast.success("Device authenticated successfully!");
        setShowAuthModal(true);
        setNewDevice({ deviceId: "", deviceName: "" });
        void fetchDevices();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to authenticate device");
    } finally {
      setAuthenticating(false);
    }
  };

  const handleDelete = async (deviceId: string) => {
    const token = sessionStorage.getItem("token");
    if (!token || !confirm(`Are you sure you want to delete device ${deviceId}?`)) {
      return;
    }

    try {
      await apiWithAuth.delete(`/whatsapp/devices/${deviceId}`);
      toast.success("Device deleted successfully");
      void fetchDevices();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to delete device");
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const onlineDevices = devices.filter((d) => d.status === "ONLINE").length;
  const totalDevices = devices.length;
  const offlineDevices = totalDevices - onlineDevices;

  return (
    <div className="space-y-6">
      {/* Enhanced Header Section */}
      <div className="bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 rounded-xl p-6 border border-green-100 dark:border-gray-700 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
              <FiMessageCircle className="text-3xl text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                WhatsApp Devices
                <Badge color="success" size="lg" className="ml-2">
                  {totalDevices} {totalDevices === 1 ? "Device" : "Devices"}
                </Badge>
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1.5 flex items-center gap-2">
                <FiShield className="text-gray-400" />
                Manage and authenticate WhatsApp devices for automated message campaigns
              </p>
            </div>
          </div>
          <Button
            gradientDuoTone="greenToGreen"
            size="lg"
            onClick={() => {
              setAuthToken(null);
              setAuthenticatedDevice(null);
              setShowAuthModal(true);
            }}
            className="shadow-md hover:shadow-lg transition-shadow"
          >
            <HiPlus className="mr-2 h-5 w-5" />
            Authenticate Device
          </Button>
        </div>
      </div>

      {/* Enhanced Stats Cards */}
      {devices.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-gradient-to-br from-green-500 to-green-600 dark:from-green-700 dark:to-green-800 border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-100 mb-1">Total Devices</p>
                <p className="text-4xl font-bold text-white">{totalDevices}</p>
                <p className="text-xs text-green-100 mt-2">Registered devices</p>
              </div>
              <div className="p-4 bg-white/20 rounded-full">
                <FiMessageCircle className="text-5xl text-white opacity-90" />
              </div>
            </div>
          </Card>

          <Card className={`bg-gradient-to-br border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 ${
            onlineDevices > 0
              ? "from-emerald-500 to-emerald-600 dark:from-emerald-700 dark:to-emerald-800"
              : "from-gray-400 to-gray-500 dark:from-gray-600 dark:to-gray-700"
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white/90 mb-1">Online Now</p>
                <p className="text-4xl font-bold text-white">{onlineDevices}</p>
                <p className="text-xs text-white/80 mt-2">Active connections</p>
              </div>
              <div className="p-4 bg-white/20 rounded-full">
                {onlineDevices > 0 ? (
                  <FiWifi className="text-5xl text-white opacity-90" />
                ) : (
                  <FiWifiOff className="text-5xl text-white opacity-90" />
                )}
              </div>
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 dark:from-orange-700 dark:to-orange-800 border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white/90 mb-1">Offline</p>
                <p className="text-4xl font-bold text-white">{offlineDevices}</p>
                <p className="text-xs text-white/80 mt-2">Inactive devices</p>
              </div>
              <div className="p-4 bg-white/20 rounded-full">
                <FiWifiOff className="text-5xl text-white opacity-90" />
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Devices List */}
      {loading && devices.length === 0 ? (
        <Card className="shadow-lg">
          <div className="flex flex-col items-center justify-center py-16">
            <Spinner size="xl" className="mb-4" />
            <span className="text-lg font-medium text-gray-600 dark:text-gray-400">Loading devices...</span>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">Please wait while we fetch your devices</p>
          </div>
        </Card>
      ) : devices.length === 0 ? (
        <Card className="shadow-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
          <div className="text-center py-16">
            <div className="mx-auto w-24 h-24 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mb-6">
              <FiMessageCircle className="h-12 w-12 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No devices found</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-md mx-auto">
              Get started by authenticating your first WhatsApp device. This will allow you to send automated messages through your campaigns.
            </p>
            <div className="flex justify-center">
              <Button
                gradientDuoTone="greenToGreen"
                size="lg"
                onClick={() => {
                  setAuthToken(null);
                  setAuthenticatedDevice(null);
                  setShowAuthModal(true);
                }}
                className="shadow-md hover:shadow-lg transition-shadow"
              >
                <HiPlus className="mr-2 h-5 w-5" />
                Authenticate Your First Device
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <FiActivity className="text-green-600 dark:text-green-400" />
                Device Management
              </h2>
              <Button
                size="sm"
                color="light"
                onClick={() => fetchDevices()}
                className="shadow-sm"
              >
                <HiRefresh className="mr-2 h-4 w-4" />
                Refresh
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table hoverable className="min-w-full">
              <Table.Head className="bg-gray-50 dark:bg-gray-800">
                <Table.HeadCell className="font-semibold text-gray-700 dark:text-gray-300">Device Name</Table.HeadCell>
                <Table.HeadCell className="font-semibold text-gray-700 dark:text-gray-300">Status</Table.HeadCell>
                <Table.HeadCell className="font-semibold text-gray-700 dark:text-gray-300">Device ID</Table.HeadCell>
                <Table.HeadCell className="font-semibold text-gray-700 dark:text-gray-300">Last Seen</Table.HeadCell>
                <Table.HeadCell className="font-semibold text-gray-700 dark:text-gray-300 text-center">Actions</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y divide-gray-200 dark:divide-gray-700">
                {devices.map((device) => (
                  <Table.Row
                    key={device.id}
                    className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Table.Cell className="whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          device.status === "ONLINE" 
                            ? "bg-green-100 dark:bg-green-900" 
                            : "bg-gray-100 dark:bg-gray-700"
                        }`}>
                          <HiOutlineDeviceMobile className={`h-5 w-5 ${
                            device.status === "ONLINE" 
                              ? "text-green-600 dark:text-green-400" 
                              : "text-gray-400"
                          }`} />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {device.deviceName || "Unnamed Device"}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">WhatsApp Device</p>
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge
                        color={device.status === "ONLINE" ? "success" : "failure"}
                        icon={device.status === "ONLINE" ? FiWifi : FiWifiOff}
                        size="lg"
                        className="font-medium"
                      >
                        {device.status}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <code className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-md text-sm font-mono text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600">
                        {device.deviceId}
                      </code>
                    </Table.Cell>
                    <Table.Cell className="whitespace-nowrap">
                      <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                        <HiClock className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">{formatDate(device.lastSeenAt)}</span>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex items-center justify-center">
                        <Button
                          size="sm"
                          color="failure"
                          onClick={() => handleDelete(device.deviceId)}
                          className="shadow-sm hover:shadow-md transition-shadow"
                        >
                          <HiTrash className="h-4 w-4" />
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        </Card>
      )}

      {/* Enhanced Authentication Modal */}
      <Modal show={showAuthModal} onClose={() => setShowAuthModal(false)} size="lg">
        <Modal.Header className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-900 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <FiShield className="text-xl text-green-600 dark:text-green-400" />
            </div>
            <span className="text-xl font-semibold text-gray-900 dark:text-white">
              Authenticate WhatsApp Device
            </span>
          </div>
        </Modal.Header>
        <Modal.Body className="p-6">
          {authToken && authenticatedDevice ? (
            <div className="space-y-6">
              <div className="p-5 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900 dark:to-emerald-900 border-2 border-green-200 dark:border-green-700 rounded-xl">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-green-100 dark:bg-green-800 rounded-full">
                    <HiCheckCircle className="text-2xl text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <span className="font-bold text-lg text-green-800 dark:text-green-200 block">
                      Device Authenticated Successfully!
                    </span>
                    <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                      Device: <strong>{authenticatedDevice.deviceName || authenticatedDevice.deviceId}</strong>
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="authToken" className="mb-3 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Device Auth Token
                </Label>
                <div className="relative">
                  <TextInput
                    id="authToken"
                    readOnly
                    value={authToken}
                    className="font-mono text-sm bg-gray-50 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 pr-12"
                  />
                  <Button
                    size="sm"
                    color="light"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={() => {
                      navigator.clipboard.writeText(authToken);
                      toast.success("Token copied to clipboard!");
                    }}
                  >
                    <HiOutlineClipboardCopy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 bg-green-50 dark:bg-green-900 p-3 rounded-lg border border-green-200 dark:border-green-700">
                  <strong>Important:</strong> Copy this token and configure it in your WhatsApp bot client. This token is required for the device to connect via WebSocket.
                </p>
              </div>
              <Button
                gradientDuoTone="greenToGreen"
                className="w-full shadow-md hover:shadow-lg transition-shadow"
                onClick={() => {
                  navigator.clipboard.writeText(authToken);
                  toast.success("Token copied to clipboard!");
                }}
              >
                <HiOutlineClipboardCopy className="mr-2 h-5 w-5" />
                Copy Token to Clipboard
              </Button>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="p-4 bg-green-50 dark:bg-green-900 rounded-lg border border-green-200 dark:border-green-700">
                <p className="text-sm text-green-800 dark:text-green-200">
                  <strong>Note:</strong> Enter a unique device ID to authenticate your WhatsApp device. The device name is optional but recommended for easier identification.
                </p>
              </div>
              <div>
                <Label htmlFor="deviceId" className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Device ID <span className="text-red-500">*</span>
                </Label>
                <TextInput
                  id="deviceId"
                  placeholder="e.g., whatsapp-device-001"
                  value={newDevice.deviceId}
                  onChange={(e) =>
                    setNewDevice({ ...newDevice, deviceId: e.target.value })
                  }
                  required
                  className="focus:ring-2 focus:ring-green-500"
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Unique identifier for your WhatsApp device (required)
                </p>
              </div>
              <div>
                <Label htmlFor="deviceName" className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Device Name <span className="text-gray-400 text-xs">(Optional)</span>
                </Label>
                <TextInput
                  id="deviceName"
                  placeholder="e.g., Office WhatsApp Bot"
                  value={newDevice.deviceName}
                  onChange={(e) =>
                    setNewDevice({ ...newDevice, deviceName: e.target.value })
                  }
                  className="focus:ring-2 focus:ring-green-500"
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  A friendly name to identify this device
                </p>
              </div>
              <Button
                gradientDuoTone="greenToGreen"
                className="w-full shadow-md hover:shadow-lg transition-shadow"
                onClick={handleAuthenticate}
                disabled={authenticating || !newDevice.deviceId.trim()}
              >
                {authenticating ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Authenticating Device...
                  </>
                ) : (
                  <>
                    <FiShield className="mr-2 h-5 w-5" />
                    Authenticate Device
                  </>
                )}
              </Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
          <Button color="gray" onClick={() => setShowAuthModal(false)} className="shadow-sm">
            {authToken ? "Close" : "Cancel"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WhatsAppDevices;
